// client/src/cognitive-flows/components/canvas/canvasExport.js
//
// Excel + PDF export for canvases, adapted from the chat report panel's
// proven exporters (ArtifactPanel.jsx) to canvas result shapes. Excel is
// SpreadsheetML 2003 (opens natively in Excel and Google Sheets), PDF is a
// print window whose chart SVGs are lifted from the live grid DOM by
// data-block-id. The one dependency, `api`, exists only to read
// api.defaults.baseURL for the image-block stream endpoint below — image
// blocks no longer carry a public GCS URL, so embedding one in a PDF or a
// PowerPoint slide (canvasPptx.js) means fetching it ourselves, with the
// session cookie, through our own access-controlled API.

import api from '../../utils/api';
import { resolveBlockCharts } from './chartResolve';

function xmlEscape(s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

function escapeHtml(s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** A block's display title — mirrors CanvasBlockCard's blockTitle. */
export function canvasBlockTitle(block) {
    if (block.type === 'agent') return block.result?.headline || 'Agent';
    if (block.type === 'workflow') return block.result?.headline || block.config?.title || 'Workflow';
    if (block.type === 'chart') return block.result?.Chart_data?.chart_header || 'Chart';
    if (block.type === 'scorecard') return block.result?.label || block.config?.label || 'Scorecard';
    if (block.type === 'table') return 'Table';
    if (block.type === 'image') return block.config?.title || block.config?.alt || 'Image';
    if (block.type === 'manualtable') return block.config?.title || 'Table';
    if (block.type === 'pivot') return block.config?.title || 'Pivot table';
    return block.config?.title || 'Text';
}

/** A workflow block whose run ended in several agents stores one section per
 *  agent (server: canvasWorkflowBlock.js#sectionsResult). */
export function hasWorkflowSections(result) {
    return Array.isArray(result?.sections) && result.sections.length > 1;
}

/**
 * The blocks an export should walk for this block: the block itself, or —
 * for a workflow that ended in several agents — one synthetic block per
 * agent, so every builder here gives each agent its own worksheet, tab,
 * printed section or slide without learning anything about sections.
 *
 * A synthetic block keeps the workflow type and layout, is titled
 * "<block title> — <agent>" (that is what canvasBlockTitle reads off
 * `result.headline`), and carries the agent's own headline as the first
 * callout line when it says more than the agent's name — so the section's
 * real headline still reaches the sheet, the page and the slide. The
 * presentation keys are blank: `vizView` and `chartSettings` belong to the
 * block, not to one of its sections. Presentation-only, never stored; the
 * result carries no `sections`, so expanding twice is a no-op.
 */
export function expandBlockSections(block) {
    if (!block || block.type !== 'workflow' || !hasWorkflowSections(block.result)) return [block];
    const parentTitle = canvasBlockTitle(block);
    return block.result.sections.map((section, i) => {
        const label = String(section?.label || `Agent ${i + 1}`);
        const headline = section?.headline && section.headline !== label ? String(section.headline) : '';
        const callouts = [headline, String(section?.callouts || '')].filter(Boolean).join('\n');
        return {
            ...block,
            id: `${block.id}:${i}`,
            config: { ...(block.config || {}), vizView: undefined, chartSettings: [] },
            result: {
                ...(block.result.fetchedAt ? { fetchedAt: block.result.fetchedAt } : {}),
                headline: `${parentTitle} — ${label}`,
                callouts,
                viz: section?.viz ?? null,
            },
        };
    });
}

/**
 * A manual table's stored `{ columns, rows }` → the array-of-objects shape
 * every renderer here already speaks. Mirrors the server's manualTableRows
 * (canvasBlockShapers.js) exactly — the two must agree, because the same block
 * is rendered by this file for Excel/PDF/PPTX and by the server for email.
 *
 * Column headers are guaranteed unique and non-empty by the server's
 * sanitizeManualTable, so these object keys cannot collide.
 */
export function manualTableRows(config) {
    const columns = Array.isArray(config?.columns) ? config.columns : [];
    const rows = Array.isArray(config?.rows) ? config.rows : [];
    if (columns.length === 0) return [];
    return rows.map((r) => {
        const out = {};
        const list = Array.isArray(r) ? r : [];
        columns.forEach((c, i) => { out[c] = list[i] ?? ''; });
        return out;
    });
}

function tableRows(rows) {
    const list = Array.isArray(rows) ? rows : [];
    // Union of keys — rows can disagree on shape and a missing key must not
    // silently drop a whole column (lesson inherited from the chat exporter).
    const headers = [...new Set(list.flatMap((r) => Object.keys(r || {})))];
    if (list.length === 0 || headers.length === 0) return [];
    return [headers, ...list.map((r) => headers.map((h) => r?.[h] ?? ''))];
}

function chartRows(cd) {
    const x = Array.isArray(cd?.charx_axis_data_points) ? cd.charx_axis_data_points : [];
    const yRaw = Array.isArray(cd?.chart_y_axis_data_points) ? cd.chart_y_axis_data_points : [];
    const y = Array.isArray(yRaw[0]) ? yRaw[0] : yRaw;
    const n = Math.min(x.length, y.length);
    if (n === 0) return [];
    // The value column is the series, so it takes series_name when the chart
    // has one — falling back to the title, then "Value", exactly as before for
    // charts that carry neither.
    const valueHeader = cd?.series_name || cd?.chart_header || 'Value';
    return [
        [cd?.x_axis_label || 'Label', valueHeader],
        ...Array.from({ length: n }, (_, i) => [x[i], y[i]]),
    ];
}

/** Header row first, then data — the spreadsheet form of each block type. */
export function canvasBlockRows(block) {
    if (block.type === 'text') {
        return String(block.config?.markdown || '').split('\n').filter((l) => l.trim()).map((l) => [l]);
    }
    if (block.type === 'image') {
        // An image has no rows — the sheet carries only its caption, if any.
        // `?? alt`, not `|| alt`: a caption cleared to "" stays cleared.
        const caption = String(block.config?.caption ?? block.config?.alt ?? '').trim();
        return caption ? [[caption]] : [];
    }
    // Hand-entered content lives in config, not in a result — it never runs.
    if (block.type === 'manualtable') return tableRows(manualTableRows(block.config));
    const r = block.result;
    if (!r || r.error) return [];
    if (block.type === 'table') return tableRows(r.rows);
    if (block.type === 'chart') {
        const [entry] = resolveBlockCharts(block);
        return chartRows(entry?.chart || r.Chart_data);
    }
    if (block.type === 'scorecard') return [[r.label || 'Value'], [r.value]];
    // A pivot is already a matrix: header row first, exactly this function's
    // contract, so it passes straight through.
    if (block.type === 'pivot') return Array.isArray(r.matrix) ? r.matrix : [];
    // agent: the viz's data plus callout lines
    const parts = [];
    // An agent result may now carry a chart AND a table. This is a
    // SPREADSHEET (Excel / Google Sheets), so the table wins whenever there is
    // one, regardless of which view the card is currently showing: chartRows
    // is a lossy two-column rendering of a chart's points, and silently
    // exporting that instead of a real table the result already holds would
    // lose data the user never sees go missing. The visual surfaces —
    // canvasPptx.js and canvasEmailHtml.js — honour `config.vizView` instead,
    // because there the point is to match what was on screen.
    const vizTable = Array.isArray(r.viz?.table) && r.viz.table.length > 0 ? r.viz.table : null;
    if (vizTable) parts.push(...tableRows(vizTable));
    else {
        // Resolved, so a reader's title or column pick exports as it renders.
        const charts = resolveBlockCharts(block).map((e) => e.chart).filter(Boolean);
        for (const cd of charts) {
            const rows = chartRows(cd);
            if (rows.length === 0) continue;
            // One blank row between charts — never a leading one, never a
            // doubled one around a chart that had no points to emit.
            if (parts.length) parts.push([]);
            parts.push(...rows);
        }
    }
    const callouts = String(r.callouts || '').split('\n').map((l) => l.trim()).filter(Boolean);
    if (callouts.length) {
        if (parts.length) parts.push([]);
        parts.push(...callouts.map((l) => [l]));
    }
    return parts;
}

// Excel sheet names: ≤31 chars, no : \ / ? * [ ], unique within the workbook.
// Exported so the Google Sheets export (buildCanvasSheetTabs below) can
// produce the EXACT same tab list — same names, same dedup — rather than a
// second implementation that could silently drift from the Excel one.
export function sheetNameFor(title, index, used) {
    const base = String(title || `Block ${index + 1}`).replace(/[:\\/?*[\]]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 31)
        || `Block ${index + 1}`;
    let name = base;
    let n = 2;
    while (used.has(name.toLowerCase())) {
        const suffix = ` (${n++})`;
        name = base.slice(0, 31 - suffix.length) + suffix;
    }
    used.add(name.toLowerCase());
    return name;
}

/** Every block → its own worksheet, in canvas order. Null when nothing to export. */
export function buildCanvasWorkbook(blocks) {
    const used = new Set();
    const cell = (v) => (typeof v === 'number' && Number.isFinite(v)
        ? `<Cell><Data ss:Type="Number">${v}</Data></Cell>`
        : `<Cell><Data ss:Type="String">${xmlEscape(v)}</Data></Cell>`);
    const row = (cells) => (cells.length === 0 ? '<Row/>' : `<Row>${cells.map((c) => cell(c)).join('')}</Row>`);

    const sheets = (blocks || []).flatMap(expandBlockSections).map((b, i) => {
        const data = canvasBlockRows(b);
        const rows = data.map((r) => row(r));
        // An empty block still gets a tab — "all blocks" means all of them,
        // and an empty <Table> is invalid SpreadsheetML.
        if (rows.length === 0) rows.push(row([canvasBlockTitle(b)]));
        return `<Worksheet ss:Name="${xmlEscape(sheetNameFor(canvasBlockTitle(b), i, used))}"><Table>${rows.join('')}</Table></Worksheet>`;
    });

    if (sheets.length === 0) return null;
    return `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">${sheets.join('')}</Workbook>`;
}

// ── Google Sheets export ─────────────────────────────────────────────────────
//
// The client composes, the server translates (design doc Decision 2): this
// builds the exact SAME one-tab-per-block layout buildCanvasWorkbook produces
// for Excel — same titles, same sheetNameFor dedup — as a plain { name, rows }
// payload the server POSTs straight into sheetsService.writeSheet's
// `rawValues` (a 2D array, header row first: exactly canvasBlockRows' shape,
// no object-keyed reshaping). Pure and DOM-free, so it is fully unit-testable.

/** A full Google document URL or a bare id → the id, or null when unparseable.
 *  Mirrors server/services/cognitive-flows/googleDocId.js EXACTLY — this copy
 *  only saves the user a round trip by rejecting garbage before it is sent;
 *  the server never trusts this one and re-parses the raw input itself. */
export function parseGoogleDocId(input) {
    const value = String(input ?? '').trim();
    if (!value) return null;
    if (/^https?:\/\//i.test(value)) {
        const m = value.match(/\/d\/([A-Za-z0-9_-]{10,})(?:[/?#]|$)/);
        return m ? m[1] : null;
    }
    return /^[A-Za-z0-9_-]{10,}$/.test(value) ? value : null;
}

/**
 * Every block → one `{ name, rows }` Sheets tab, in canvas order. `rows` is
 * canvasBlockRows' raw 2D array unchanged — an empty block still gets a tab
 * (its title, alone in row 1), matching buildCanvasWorkbook's empty-<Table>
 * fallback, because sheetsService.writeSheet silently skips creating a tab
 * altogether when handed zero rows.
 */
export function buildCanvasSheetTabs(blocks) {
    const used = new Set();
    return (blocks || []).flatMap(expandBlockSections).map((b, i) => {
        const rows = canvasBlockRows(b);
        const title = canvasBlockTitle(b);
        return {
            name: sheetNameFor(title, i, used),
            rows: rows.length > 0 ? rows : [[title]],
        };
    });
}

// ── Image blocks: fetched, not linked ───────────────────────────────────────
//
// Image blocks stream through GET .../blocks/:blockId/image — an
// access-controlled endpoint requiring the session cookie, not a public GCS
// URL any more. Neither the PDF print window nor pptxgenjs (canvasPptx.js)
// can authenticate a request THEY issue (a plain <img src> in a fresh
// about:blank print document, or pptxgenjs' own internal `path` fetch), so
// both exporters fetch the bytes here, with credentials, and embed the
// result as a data URL instead.

/**
 * One image block's bytes, fetched through the stream endpoint and converted
 * to a data URL. Resolves null on ANY failure (network error, 404, GCP
 * disabled) — callers degrade gracefully, the same as a chart that could not
 * be rasterised: the block gets its empty-state message instead of a broken
 * picture.
 */
export async function fetchCanvasImageDataUrl(url) {
    try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
}

/**
 * Every image block with a storagePath → { blockId: dataUrl }, for the PDF
 * print path (canvasPptx.js's renderPptx fetches its own, one block at a
 * time, since buildSlideSpec must stay pure/DOM-free). A block with no
 * storagePath, or whose fetch failed, is simply absent from the map —
 * buildCanvasPrintHtml treats a missing entry the same as an unconfigured
 * block.
 */
export async function collectCanvasBlockImages(canvasId, blocks) {
    const targets = (blocks || []).filter((b) => b.type === 'image' && b.config?.storagePath);
    const out = {};
    await Promise.all(targets.map(async (b) => {
        const url = `${api.defaults.baseURL}/canvases/${canvasId}/blocks/${b.id}/image`;
        const dataUrl = await fetchCanvasImageDataUrl(url);
        if (dataUrl) out[b.id] = dataUrl;
    }));
    return out;
}

/** Print-ready HTML of the canvas; chart SVGs come from the live grid DOM,
 *  image blocks from the pre-fetched `blockImages` map (collectCanvasBlockImages). */
export function buildCanvasPrintHtml(title, blocks, svgById, blockImages, logos) {
    // Sections of a multi-agent workflow print one after another; their
    // synthetic ids never match a captured SVG (rasters are per block).
    const body = (blocks || []).flatMap(expandBlockSections).map((b) => {
        const parts = [`<h2>${escapeHtml(canvasBlockTitle(b))}</h2>`];
        const svg = svgById[b.id];
        if (svg) parts.push(`<div class="chart">${svg}</div>`);
        const r = b.result;
        if (b.type === 'text') {
            parts.push(`<p class="text">${escapeHtml(b.config?.markdown || '')}</p>`);
        } else if (b.type === 'image') {
            const cfgImg = b.config || {};
            const dataUrl = (blockImages || {})[b.id];
            if (dataUrl) {
                const fitStyle = cfgImg.fit === 'cover' ? 'object-fit:cover;' : 'object-fit:contain;';
                parts.push(`<img class="cvx-image" src="${escapeHtml(dataUrl)}" alt="${escapeHtml(cfgImg.caption ?? cfgImg.alt ?? '')}" style="max-width:100%;height:auto;${fitStyle}" />`);
            }
            const caption = cfgImg.caption ?? cfgImg.alt ?? '';
            if (caption) parts.push(`<p class="text">${escapeHtml(caption)}</p>`);
        } else if (b.type === 'manualtable') {
            const rows = manualTableRows(b.config);
            if (rows.length > 0) {
                const headers = Object.keys(rows[0]);
                parts.push(`<table><thead><tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead><tbody>${
                    rows.map((x) => `<tr>${headers.map((h) => `<td>${escapeHtml(x?.[h])}</td>`).join('')}</tr>`).join('')
                }</tbody></table>`);
            }
        } else if (b.type === 'pivot' && r && !r.error) {
            // A pivot's matrix is a 2D array (header row first), not the
            // array-of-objects every other table here is, so it gets its own
            // few lines rather than being forced through the shape below.
            const m = Array.isArray(r.matrix) ? r.matrix : [];
            if (m.length > 1) {
                const [head, ...rest] = m;
                parts.push(`<table><thead><tr>${head.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead><tbody>${
                    rest.map((row) => `<tr>${row.map((c) => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`).join('')
                }</tbody></table>`);
            }
        } else if (r && !r.error) {
            // Not gated on viz.type: an agent result can now hold a chart AND
            // a table, and a printed page has room for both — the chart SVG is
            // already pushed above. On the card they are alternatives only
            // because a dashboard tile is small.
            const rows = b.type === 'table'
                ? r.rows
                : ((b.type === 'agent' || b.type === 'workflow') && Array.isArray(r.viz?.table) ? r.viz.table : null);
            if (Array.isArray(rows) && rows.length > 0) {
                const headers = [...new Set(rows.flatMap((x) => Object.keys(x || {})))];
                parts.push(`<table><thead><tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead><tbody>${
                    rows.map((x) => `<tr>${headers.map((h) => `<td>${escapeHtml(x?.[h])}</td>`).join('')}</tr>`).join('')
                }</tbody></table>`);
            }
            if (b.type === 'scorecard') parts.push(`<p class="score">${escapeHtml(Number(r.value).toLocaleString())} <span>${escapeHtml(r.label || '')}</span></p>`);
            if ((b.type === 'agent' || b.type === 'workflow') && r.callouts) parts.push(`<p class="callouts">${escapeHtml(r.callouts)}</p>`);
        }
        return `<section class="block">${parts.join('')}</section>`;
    }).join('');
    return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
        body { font-family: Inter, Arial, sans-serif; color: #16233b; margin: 36px; }
        h1 { font-size: 22px; margin: 0 0 4px; }
        .meta { font-size: 11px; color: #5c6b84; margin: 0 0 24px; }
        .block { page-break-inside: avoid; border-bottom: 1px solid #e3e6ec; padding: 16px 0; }
        h2 { font-size: 15px; margin: 0 0 10px; }
        .chart { margin: 0 0 10px; }
        .chart svg { max-width: 100%; height: auto; }
        table { border-collapse: collapse; font-size: 11.5px; width: 100%; margin: 0 0 10px; }
        th, td { border: 1px solid #e3e6ec; padding: 5px 8px; text-align: left; }
        th { background: #fafaf9; }
        .score { font-size: 26px; font-weight: 700; margin: 0 0 10px; font-variant-numeric: tabular-nums; }
        .score span { font-size: 12px; font-weight: 600; color: #5c6b84; text-transform: uppercase; letter-spacing: 0.06em; }
        p.callouts, p.text { white-space: pre-wrap; font-size: 12.5px; line-height: 1.7; color: #33415c; margin: 0; }
        .brandbar { display: flex; align-items: center; justify-content: space-between; margin: 0 0 20px; }
        .brandbar img { max-height: 44px; max-width: 240px; width: auto; height: auto; }
    </style></head><body>${brandBarHtml(logos)}<h1>${escapeHtml(title)}</h1><p class="meta">Exported ${new Date().toLocaleString()}</p>${body}</body></html>`;
}

/**
 * The two-logo header for the print sheet. Uses flex rather than the email's
 * table layout — this renders in a real browser, not a mail client — and
 * emits an empty spacer for a missing side so a single logo stays on its own
 * edge instead of drifting to the centre.
 */
function brandBarHtml(logos) {
    const left = logos?.left;
    const right = logos?.right;
    if (!left && !right) return '';
    const img = (src) => (src ? `<img src="${escapeHtml(src)}" alt="" />` : '<span></span>');
    return `<div class="brandbar">${img(left)}${img(right)}</div>`;
}

/**
 * Both branding logos as data URLs, ready to inline. Logos are private objects
 * streamed through our API (same as image blocks), so a print window — which
 * has no session — cannot fetch them by URL; we fetch them here and embed the
 * bytes. Returns {} when nothing is configured.
 */
export async function collectBrandingLogos() {
    const base = api.defaults.baseURL;
    const [left, right] = await Promise.all([
        fetchCanvasImageDataUrl(`${base}/settings/branding/logo/left`),
        fetchCanvasImageDataUrl(`${base}/settings/branding/logo/right`),
    ]);
    return { ...(left ? { left } : {}), ...(right ? { right } : {}) };
}

// ── Chart rasters for email ─────────────────────────────────────────────────
//
// The PDF path can hand the print window raw SVG; email cannot. Gmail strips
// inline <svg> and blocks data: image URIs, so a chart only survives the trip
// as a real PNG attachment. Recharts draws in the browser and there is no
// headless renderer server-side, so the pixels have to be produced here and
// posted up with the send request.

const SVG_NS = 'http://www.w3.org/2000/svg';
const CHART_PIXEL_RATIO = 2;   // retina-density, so mail clients can downscale cleanly
const RASTER_TIMEOUT_MS = 4000;

/** One chart SVG → a PNG data URL. Resolves null on any failure — never throws. */
function svgToPngDataUrl(svgEl) {
    return new Promise((resolve) => {
        let settled = false;
        const done = (v) => { if (!settled) { settled = true; resolve(v); } };
        // A chart that never decodes must not hang the whole send.
        const timer = setTimeout(() => done(null), RASTER_TIMEOUT_MS);
        try {
            const rect = svgEl.getBoundingClientRect();
            const w = Math.max(1, Math.round(Number(svgEl.getAttribute('width')) || rect.width));
            const h = Math.max(1, Math.round(Number(svgEl.getAttribute('height')) || rect.height));

            const clone = svgEl.cloneNode(true);
            clone.setAttribute('xmlns', SVG_NS);
            clone.setAttribute('width', String(w));
            clone.setAttribute('height', String(h));
            // Recharts takes tick/label typography from the page stylesheet, but
            // the rasterizer renders this SVG as a standalone document with no
            // page CSS attached — without baking the font in, every label falls
            // back to the UA default and the chart comes out visibly wrong.
            const style = document.createElementNS(SVG_NS, 'style');
            style.textContent = "text{font-family:Inter,Arial,Helvetica,sans-serif;font-size:11px;}";
            clone.insertBefore(style, clone.firstChild);

            const img = new Image();
            img.onload = () => {
                clearTimeout(timer);
                try {
                    const canvasEl = document.createElement('canvas');
                    canvasEl.width = w * CHART_PIXEL_RATIO;
                    canvasEl.height = h * CHART_PIXEL_RATIO;
                    const ctx = canvasEl.getContext('2d');
                    ctx.scale(CHART_PIXEL_RATIO, CHART_PIXEL_RATIO);
                    // Charts are drawn for a white card. Left transparent, the
                    // alpha renders black in dark-mode mail clients.
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, w, h);
                    ctx.drawImage(img, 0, 0, w, h);
                    done(canvasEl.toDataURL('image/png'));
                } catch { done(null); }
            };
            img.onerror = () => { clearTimeout(timer); done(null); };
            // encodeURIComponent rather than btoa: chart labels are arbitrary
            // text and btoa throws on anything outside Latin-1.
            img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
        } catch {
            clearTimeout(timer);
            done(null);
        }
    });
}

/** Every rendered chart in the grid → { blockId: pngDataUrl }. Skips what won't rasterize. */
export async function collectCanvasChartImages(gridEl) {
    const cards = Array.from(gridEl?.querySelectorAll('[data-block-id]') || []);
    const out = {};
    await Promise.all(cards.map(async (el) => {
        // Charts only — a bare `svg` query would grab the card's grip/type icons.
        const svg = el.querySelector('.recharts-wrapper svg');
        if (!svg) return;
        const png = await svgToPngDataUrl(svg);
        if (png) out[el.getAttribute('data-block-id')] = png;
        // svgToPngDataUrl resolves null on a timeout or a tainted canvas, and
        // a silently missing raster is indistinguishable downstream from a
        // block that never had a chart — the exported slide just quietly has
        // no picture. Say which block, so the next occurrence is diagnosable
        // instead of a guess.
        else console.warn(`[CF_CANVAS_EXPORT] chart raster failed for block ${el.getAttribute('data-block-id')} — its exports will have no chart image`);
    }));
    return out;
}

/** Trigger a browser download of the workbook. Returns false when empty. */
export function downloadCanvasExcel(name, blocks) {
    const xml = buildCanvasWorkbook(blocks);
    if (!xml) return false;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([xml], { type: 'application/vnd.ms-excel' }));
    a.download = `${String(name || 'canvas').replace(/[^\w-]+/g, '_')}.xls`;
    a.click();
    // Deferred: a synchronous revoke can abort the download in Safari/Firefox.
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
    return true;
}

/** Whether a single block has anything a spreadsheet could hold. */
export function blockHasSpreadsheetRows(block) {
    return expandBlockSections(block).some((b) => canvasBlockRows(b).length > 0);
}

// Block types whose card carries its own Download (next to the chart or above
// the table) — the settings drawer must not offer a second one for them.
const CARD_DOWNLOAD_TYPES = new Set(['agent', 'workflow']);

/** Whether the settings drawer should offer "Download (Excel)" for this block. */
export function drawerOffersDownload(block) {
    if (!block || CARD_DOWNLOAD_TYPES.has(block.type)) return false;
    return blockHasSpreadsheetRows(block);
}

/** Download ONE block's content as a workbook named after the block — one
 *  sheet, or one per agent for a workflow that ended in several (the
 *  workbook builder expands the sections itself). */
export function downloadBlockSpreadsheet(block) {
    if (!blockHasSpreadsheetRows(block)) return false;
    return downloadCanvasExcel(canvasBlockTitle(block), [block]);
}

/**
 * Open the print window and populate it. Returns false when pop-ups are
 * blocked. `window.open` fires FIRST, synchronously, before any `await` —
 * calling it after an async gap risks the popup blocker treating the call as
 * no longer "in response to" the click that triggered this export (the
 * original synchronous-open behavior is preserved on purpose); the image
 * fetch (collectCanvasBlockImages, needs `canvasId`) happens after, writing
 * into the already-open window once it resolves. `canvasId` is optional —
 * omitted (e.g. a caller with no owner-scoped id to fetch through), image
 * blocks are simply left out of the printed page, same as any other export
 * failure mode.
 */
export async function printCanvasPdf(name, blocks, gridEl, canvasId, logos) {
    const win = window.open('', '_blank');
    if (!win) return false;
    const svgById = {};
    gridEl?.querySelectorAll('[data-block-id]').forEach((el) => {
        // Charts only: a bare `svg` query would grab the card's grip/type
        // ICONS first (recharts wraps its chart in .recharts-wrapper).
        const svg = el.querySelector('.recharts-wrapper svg');
        if (svg) svgById[el.getAttribute('data-block-id')] = svg.outerHTML;
    });
    const blockImages = canvasId ? await collectCanvasBlockImages(canvasId, blocks) : {};
    win.document.write(buildCanvasPrintHtml(name || 'Canvas', blocks, svgById, blockImages, logos));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
    return true;
}
